import { createContext, useContext, useEffect, useState, useCallback } from "react";
import api from "@/lib/api";

const AuthContext = createContext(null);
const PERM_KEYS = ["view_logs", "product_create", "product_edit", "product_delete", "customer_delete", "quote_delete_any", "quote_status_any", "quote_email_any", "view_staff", "view_settings", "view_profit", "quote_edit_any"];
const EMPTY_ROLE_PERMS = Object.fromEntries(PERM_KEYS.map((k) => [k, false]));
const DEFAULT_PERMS = { manager: { ...EMPTY_ROLE_PERMS }, staff: { ...EMPTY_ROLE_PERMS } };

function normalizePerms(p) {
  p = p || {};
  if (!p.manager && !p.staff && Object.keys(p).some((k) => k.startsWith("staff_"))) {
    const legacy = Object.fromEntries(Object.entries(p).map(([k, v]) => [k.replace(/^staff_/, ""), v]));
    return { manager: { ...EMPTY_ROLE_PERMS, ...legacy }, staff: { ...EMPTY_ROLE_PERMS, ...legacy } };
  }
  return {
    manager: { ...EMPTY_ROLE_PERMS, ...(p.manager || {}) },
    staff: { ...EMPTY_ROLE_PERMS, ...(p.staff || {}) },
  };
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null=checking, false=guest, obj=user
  const [perms, setPerms] = useState(DEFAULT_PERMS);
  const [logoUrl, setLogoUrl] = useState("");
  const [loading, setLoading] = useState(true);

  const loadPerms = useCallback(async () => {
    try {
      const { data } = await api.get("/settings");
      setPerms(normalizePerms(data.permissions));
      setLogoUrl(data.logo_url || "");
    } catch { /* ignore */ }
  }, []);

  useEffect(() => {
    const token = localStorage.getItem("konzade_token");
    if (!token) { setUser(false); setLoading(false); return; }
    api.get("/auth/me")
      .then(async (r) => { setUser(r.data); await loadPerms(); })
      .catch(() => setUser(false))
      .finally(() => setLoading(false));
  }, [loadPerms]);

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    localStorage.setItem("konzade_token", data.access_token);
    setUser(data.user);
    await loadPerms();
    return data.user;
  };

  const logout = () => {
    localStorage.removeItem("konzade_token");
    setUser(false);
    window.location.href = "/login";
  };

  const isAdmin = user && user.role === "admin";
  const can = (key) => {
    if (isAdmin) return true;
    if (!user) return false;
    const role = user.role === "manager" ? "manager" : "staff";
    return !!(perms[role] || {})[key];
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, logout, isAdmin, perms, can, logoUrl, refreshPerms: loadPerms }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
